import { baseApi } from "./api";
import request from "../request";

export const ticketCategoriesApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectTicketCategories: build.query({
			query: ({ projectId, ...params }) => {
				const hasParams = params && Object.keys(params).length > 0;
				return {
					url: `projects/${projectId}/ticket-categories`,
					params: hasParams ? params : undefined,
				};
			},
			providesTags: (result, _e, { projectId }) => [
				{ type: "TicketCategory", id: projectId },
				...((result?.items ?? []).map((c) => ({
					type: "TicketCategory",
					id: c.id,
				}))),
			],
		}),
		getProjectTicketCategoriesWithLVStatus: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/ticket-categories-with-lv-status`,
			}),
			providesTags: (_r, _e, projectId) => [
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
		createTicketCategory: build.mutation({
			query: ({ projectId, ...body }) => ({
				url: `projects/${projectId}/ticket-categories`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "TicketCategory", id: projectId },
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
		updateTicketCategory: build.mutation({
			query: ({ projectId, categoryId, ...body }) => ({
				url: `projects/${projectId}/ticket-categories/${categoryId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, categoryId }) => [
				{ type: "TicketCategory", id: projectId },
				{ type: "TicketCategory", id: categoryId },
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
		deleteTicketCategory: build.mutation({
			query: ({ projectId, categoryId }) => ({
				url: `projects/${projectId}/ticket-categories/${categoryId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, { projectId, categoryId }) => [
				{ type: "TicketCategory", id: projectId },
				{ type: "TicketCategory", id: categoryId },
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
	}),
});

export const {
	useGetProjectTicketCategoriesQuery,
	useGetProjectTicketCategoriesWithLVStatusQuery,
	useCreateTicketCategoryMutation,
	useUpdateTicketCategoryMutation,
	useDeleteTicketCategoryMutation,
} = ticketCategoriesApi;

// ────────────────────────────────────────────────────────────────────────────
// Formatter helpers - pure JS, components wrap in useMemo as needed.
// ────────────────────────────────────────────────────────────────────────────

/** Standard dropdown: disabled when an LV exists OR no company is assigned. */
export const formatTicketCategoriesForDropdown = (categories) =>
	(categories || []).map((cat) => ({
		value: cat.id,
		label: cat.name,
		isDisabled: cat.hasLV || !cat.hasCompanyAssigned,
	}));

/** Nachtrag flow: only checks company assignment; multiple LVs per category allowed. */
export const formatTicketCategoriesForNachtrag = (categories) =>
	(categories || []).map((cat) => ({
		value: cat.id,
		label: cat.name,
		isDisabled: !cat.hasCompanyAssigned,
	}));

// ────────────────────────────────────────────────────────────────────────────
// Blob/PDF download - plain helper (DOM side-effects, doesn't fit RTK cache).
// ────────────────────────────────────────────────────────────────────────────

export const downloadTicketCategoryPdf = async (id, params = {}, projectId) => {
	const lang = params?.lang ?? params ?? "de";
	const categoryName = params?.categoryName ?? "";
	try {
		const config = {
			responseType: "blob",
			headers: { Accept: "application/pdf" },
		};
		const response = await request.get(
			`projects/${projectId}/ticket-categories/${id}/download?lang=${lang}`,
			config,
		);
		const blob = new Blob([response], { type: "application/pdf" });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		const safeName = categoryName
			? `gewerk-${categoryName}.pdf`
			: `gewerk-${id}.pdf`;
		link.setAttribute("download", safeName);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
		return response;
	} catch (error) {
		console.error("Error downloading ticket category PDF:", error);
		throw new Error(error?.message || "Error downloading PDF");
	}
};
